/**
 * 🔑 Google OAuth Access Token Manager
 * 
 * Verwaltet den Google OAuth Access Token für Google Drive API Zugriffe.
 * Der Token wird beim Google Sign-In mit dem Scope 'drive.file' angefordert.
 * 
 * Token-Lebenszyklus:
 * - Wird beim Login gesetzt (signInWithGoogle mit scopes)
 * - Verfällt nach ~1 Stunde
 * - Wird bei 401-Fehlern automatisch erneuert via Re-Auth
 */

let _accessToken = null;
let _tokenTimestamp = null;

// Token-Lebensdauer: 55 Minuten (5 Min Puffer vor Ablauf)
const TOKEN_LIFETIME_MS = 55 * 60 * 1000;

// 🔧 v1.47: Token aus sessionStorage wiederherstellen (überlebt WebView-Navigierungen)
try {
  const stored = sessionStorage.getItem('_googleAccessToken');
  const storedTs = sessionStorage.getItem('_googleTokenTimestamp');
  if (stored && storedTs) {
    const age = Date.now() - parseInt(storedTs, 10);
    if (age < TOKEN_LIFETIME_MS) {
      _accessToken = stored;
      _tokenTimestamp = parseInt(storedTs, 10);
      console.log('🔑 Google Access Token aus sessionStorage wiederhergestellt');
    } else {
      sessionStorage.removeItem('_googleAccessToken');
      sessionStorage.removeItem('_googleTokenTimestamp');
    }
  }
} catch (e) { /* sessionStorage nicht verfügbar */ }

/**
 * Setzt den Google OAuth Access Token
 * @param {string} token - Der Access Token von signInWithGoogle
 */
export function setGoogleAccessToken(token) {
  _accessToken = token;
  _tokenTimestamp = Date.now();
  // 🔧 v1.47: Auch in sessionStorage speichern
  try {
    sessionStorage.setItem('_googleAccessToken', token);
    sessionStorage.setItem('_googleTokenTimestamp', _tokenTimestamp.toString());
  } catch (e) { /* ignore */ }
  console.log('🔑 Google Access Token gesetzt');
}

/**
 * Gibt den aktuellen Access Token zurück
 * @returns {string|null}
 */
export function getGoogleAccessToken() {
  return _accessToken;
}

/**
 * Prüft ob der Token wahrscheinlich noch gültig ist
 * @returns {boolean}
 */
export function isTokenValid() {
  if (!_accessToken || !_tokenTimestamp) return false;
  return (Date.now() - _tokenTimestamp) < TOKEN_LIFETIME_MS;
}

/**
 * Löscht den gespeicherten Token (bei Logout)
 */
export function clearGoogleAccessToken() {
  _accessToken = null;
  _tokenTimestamp = null;
  try {
    sessionStorage.removeItem('_googleAccessToken');
    sessionStorage.removeItem('_googleTokenTimestamp');
  } catch (e) { /* ignore */ }
  console.log('🔑 Google Access Token gelöscht');
}

/**
 * 🔧 v1.47: Holt ein frisches Access Token vom Server
 * Wird bei App-Start aufgerufen wenn User bereits eingeloggt ist
 * 
 * @returns {Promise<string|null>} Das neue Token oder null
 */
export async function refreshGoogleAccessToken() {
  try {
    // Token vom Server holen
    const { getDriveToken } = await import('./apiService');
    const token = await getDriveToken();
    if (token) {
      setGoogleAccessToken(token);
      console.log('🔑 ✅ Drive Token vom Server erneuert');
      return token;
    }
    return null;
  } catch (error) {
    console.warn('🔑 ⚠️ Token-Refresh fehlgeschlagen (nicht kritisch):', error.message || error);
    return null;
  }
}
